import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize, takeUntil } from "rxjs/operators";
import { SubscribingComponent } from "app/common";
import { StatementService } from "app/statements/statement.service";
import { Statement } from "app/statements";
import { WorkflowControllerService } from "app/core/workflow/workflow-controller/workflow-controller.service";
import { DocumentRequest, DocumentRequestResponse } from "app/statements/document-request";
import { ToolsBarButtonInterface } from "app/shared/components/tools-bar/tools-bar.component";
import { PackageOptions } from "app/statements/package-options";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-tax-documents",
  templateUrl: "./tax-documents.component.html",
  styleUrls: ["./tax-documents.component.scss"],
  standalone: false
})
export class TaxDocumentsComponent extends SubscribingComponent implements OnInit {
  public statementId: number;
  public statement: Statement;
  public documentRequest: DocumentRequest | null = null;
  public clientNote = "";
  public contentLoaded = false;
  public loadingStatementData = false;
  public loadingDocumentRequest = false;
  public isSubmitting = false;
  public errorMessage = "";
  public buttons: ToolsBarButtonInterface[] = [];

  private isClientNoteDirty = false;
  private uploadingItemIds: Set<number> = new Set<number>();
  private deletingFileIds: Set<number> = new Set<number>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private statementService: StatementService,
    private translate: TranslateService,
    public workFlowController: WorkflowControllerService
  ) {
    super();
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params) => {
      this.statementId = parseInt(params["statementId"], 10);
      this.getData();
    });

    this.setButtons();
  }

  public onClientNoteChange(note: string): void {
    this.clientNote = note;
    this.isClientNoteDirty = true;
  }

  public onFilesSelected(itemId: number, event: Event): void {
    if (this.isReadOnly || this.isSubmitting || !this.statement?.id) return;

    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    input.value = "";
    if (!files.length) return;

    this.errorMessage = "";
    this.uploadingItemIds.add(itemId);

    this.statementService
      .uploadDocumentRequestFiles(this.statement.id, itemId, files)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => this.uploadingItemIds.delete(itemId))
      )
      .subscribe({
        next: () => this.loadDocumentRequest(),
        error: (error: any) => (this.errorMessage = this.extractErrorMessage(error))
      });
  }

  public removeFile(fileId: number): void {
    if (this.isReadOnly || this.isSubmitting || !this.statement?.id) return;

    this.errorMessage = "";
    this.deletingFileIds.add(fileId);

    this.statementService
      .deleteDocumentRequestFile(this.statement.id, fileId)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => this.deletingFileIds.delete(fileId))
      )
      .subscribe({
        next: () => this.loadDocumentRequest(),
        error: (error: any) => (this.errorMessage = this.extractErrorMessage(error))
      });
  }

  public isItemUploading(itemId: number): boolean {
    return this.uploadingItemIds.has(itemId);
  }

  public isFileDeleting(fileId: number): boolean {
    return this.deletingFileIds.has(fileId);
  }

  public proceed(): void {
    if (this.isProceedDisabled) return;

    if (this.isReadOnly) {
      this.goNextStep();
      return;
    }

    this.errorMessage = "";
    this.isSubmitting = true;

    this.statementService
      .submitDocumentRequest(this.statement.id, this.clientNote?.trim() || "")
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe({
        next: () => {
          if (this.statement) {
            this.statement.hasOpenDocumentRequest = false;
          }
          this.goNextStep();
        },
        error: (error: any) => (this.errorMessage = this.extractErrorMessage(error))
      });
  }

  public goBack(): void {
    this.workFlowController.goToThePreviousStep();
  }

  public get isReadOnly(): boolean {
    return !!this.documentRequest?.submitted_at;
  }

  public get isProceedDisabled(): boolean {
    if (this.loadingStatementData || this.loadingDocumentRequest || this.isSubmitting) {
      return true;
    }
    if (!this.documentRequest) {
      return true;
    }
    if (this.isAnyFileActionInProgress) {
      return true;
    }

    return !this.isReadOnly && !this.hasAnyUploadedFiles;
  }

  private get hasAnyUploadedFiles(): boolean {
    if (!this.documentRequest?.items?.length) return false;
    return this.documentRequest.items.some((item) => (item.files?.length || 0) > 0);
  }

  private get isAnyFileActionInProgress(): boolean {
    return this.uploadingItemIds.size > 0 || this.deletingFileIds.size > 0;
  }

  private getData(): void {
    this.loadingStatementData = true;
    this.contentLoaded = false;
    this.errorMessage = "";

    this.statementService
      .getStatement(this.statementId)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => (this.loadingStatementData = false))
      )
      .subscribe({
        next: (statement: Statement) => {
          this.statement = statement;
          this.workFlowController.init(this.statement);

          if (!this.isStepRequired()) {
            this.goNextStep();
            return;
          }

          this.loadDocumentRequest();
        },
        error: () => {
          this.contentLoaded = true;
          this.router.navigate(["/new"]);
        }
      });
  }

  private isStepRequired(): boolean {
    return (
      !!this.statement &&
      this.statement.serviceType === PackageOptions.tax_adviser &&
      !!this.statement.vollmachtSigned &&
      !!this.statement.hasOpenDocumentRequest
    );
  }

  private loadDocumentRequest(): void {
    if (!this.statement?.id) return;

    this.loadingDocumentRequest = true;
    this.errorMessage = "";

    this.statementService
      .getDocumentRequest(this.statement.id)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.loadingDocumentRequest = false;
          this.contentLoaded = true;
        })
      )
      .subscribe({
        next: (response: DocumentRequestResponse) => {
          this.documentRequest = response?.document_request || null;
          if (!this.isClientNoteDirty) {
            this.clientNote = this.documentRequest?.client_note || "";
          }

          if (!this.documentRequest) {
            this.goNextStep();
          }
        },
        error: (error: any) => {
          this.documentRequest = null;
          this.errorMessage = this.extractErrorMessage(error);
        }
      });
  }

  private setButtons(): void {
    this.buttons = [
      {
        type: "back",
        label: "COMMON.BACK",
        action: this.goBack.bind(this)
      },
      {
        type: "proceed",
        label: "COMMON.PROCEED",
        action: this.proceed.bind(this),
        disabled: () => this.isProceedDisabled
      }
    ];
  }

  private goNextStep(): void {
    this.workFlowController.goToTheNextStep();
  }

  private extractErrorMessage(error: any): string {
    const message = this.parseErrorValue(error?.error);
    return message || this.translate.instant("TAX_DOCUMENTS.ERROR_GENERIC");
  }

  private parseErrorValue(value: unknown): string {
    if (!value) return "";
    if (typeof value === "string") return value;

    if (Array.isArray(value)) {
      for (const currentValue of value) {
        const message = this.parseErrorValue(currentValue);
        if (message) return message;
      }
      return "";
    }

    if (typeof value === "object") {
      const record = value as Record<string, unknown>;
      const importantFields = ["detail", "message", "error", "non_field_errors"];

      for (const fieldName of importantFields) {
        if (record[fieldName] !== undefined) {
          const message = this.parseErrorValue(record[fieldName]);
          if (message) return message;
        }
      }

      for (const fieldName of Object.keys(record)) {
        const message = this.parseErrorValue(record[fieldName]);
        if (message) return message;
      }
    }

    return "";
  }
}
